import DateFnsUtils from '@date-io/date-fns';
import { Button, createStyles, Grid, InputAdornment, ListItemText, makeStyles, MenuItem, TextField, Theme } from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import sicrediStyles from 'assets/styles/default.scss';
import { CalendarIcon, CircleCheckIcon, InvertedChevronIcon, SyncIcon } from 'components/Icons';
import CircleCloseIcon from 'components/Icons/CircleCloseIcon';
import ptBR from 'date-fns/locale/pt-BR';
import { AssembleiaAssessorRoutePath } from 'library/Routes';
import AssembleiaProduto from 'models/AssembleiaProduto';
import AssembleiaProdutosPorTipo from 'models/AssembleiaProdutosPorTipo';
import AssembleiaProdutoTipo from 'models/AssembleiaProdutoTipo';
import AssembleiaTipo from 'models/AssembleiaTipo';
import CadastroAssembleia from 'models/CadastroAssembleia';
import RetornoCadastroAssembleia from 'models/RetornoCadastroAssembleia';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  assembleiasAssessorCadastrar,
  assembleiasAssessorEditar,
  assembleiasAssessorLoadProdutosPorTipo,
  assembleiasAssessorLoadTipos,
  assembleiasAssessorLoadTiposProduto
} from 'store/modules/AssembleiasAssessorActions';
import { IAssembleiasAssessorEvent } from 'store/modules/AssembleiasAssessorReducer';
import { modalsActionsClose } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';
import AxiosErrorHandler from 'utils/AxiosErrorHandler';
import { inputTimeMask, isAValidDate, isAValidTime, localeDateToISO } from 'utils/DateUtils';
import { isAValidVideoUrl } from 'utils/PlayerUtils';
import { getToastSuccess } from 'utils/ToasterUtils';
import { IModalProps } from '../../store/modules/ModalsReducer';
import GenericModal, { IGenericModalProps } from '../GenericModal/GenericModal';

const MAX_NAME_SIZE = 200;
const MAX_DESCRICAO_SIZE = 500;

export interface IAssembleiaEditModalGenericProps extends IGenericModalProps {
  key: string;
  assembleia?: IAssembleiasAssessorEvent;
}

export interface IAssembleiaEditModal extends IModalProps {
  genericProps?: IAssembleiaEditModalGenericProps;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectRoot: {
      marginTop: '10px',
      width: '100% !important',
    },
    dropdownIcon: {
      fontSize: 16,
      color: sicrediStyles.primarySicredi,
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    leftActionsContent: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      [theme.breakpoints.down('xs')]: {
        alignItems: 'stretch',
      },
    },
    rightActionsContent: {
      display: 'flex',
      alignItems: 'flex-end',
      flexDirection: 'column',
      [theme.breakpoints.down('xs')]: {
        alignItems: 'stretch',
        paddingTop: '10px',
      },
    },
    nomeHelperText: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
    },
    nomeHelperError: {
      flex: 'auto',
    },
    nomeHelperCounter: {
      color: sicrediStyles.secondarySicredi,
    },
    dateField: {
      cursor: 'pointer',
      marginTop: '10px',
    },
    dateFieldAdornment: {
      color: sicrediStyles.primarySicredi,
      marginLeft: theme.spacing(-3),
      pointerEvents: 'none',
    }
  })
);

const ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID = 'tipo';
const ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID = 'tipoProduto';
const ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID = 'produtos';
const ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID = 'titulo';
const ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID = 'descricao';
const ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID = 'urlTransmissao';
const ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID = 'data';
const ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID = 'horario';
const ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID = 'dataFim';
const ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID = 'horarioFim';

let defaultValues: any = {};
defaultValues[ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID] = '';
defaultValues[ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID] = '';
defaultValues[ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID] = '';
defaultValues[ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID] = '';
defaultValues[ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID] = '';
defaultValues[ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID] = '';
defaultValues[ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID] = moment().format();
defaultValues[ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID] = '';
defaultValues[ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID] = moment().format();
defaultValues[ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID] = '';

export default ({ genericProps }: IAssembleiaEditModal) => {
  const { t } = useTranslation();
  const {
    loadingTipos,
    tipos,
    loadingTiposProduto,
    tiposProduto,
    loadingProdutosPorTipo,
    produtosPorTipo,
  } = useSelector((state: IReducer) => state.assembleiasAssessor);
  const classes = useStyles();
  const history = useHistory();
  const initialValues = useMemo<any>(() => {
    const currEvento: IAssembleiasAssessorEvent | undefined = genericProps?.assembleia;
    const tipoProduto: AssembleiaProdutoTipo | undefined = currEvento && currEvento.produtos.length > 0 ? currEvento.produtos[0].tipoProduto! : undefined;
    const momentDataInicio: Moment | null = currEvento?.dataEvento ? moment(currEvento?.dataEvento) : null;
    const momentDataFim: Moment | null = currEvento?.dataEventoFim ? moment(currEvento?.dataEventoFim) : null;
    return {
      [ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID]: currEvento?.tipoAssembleia.id || defaultValues[ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID]: tipoProduto?.id || defaultValues[ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID]: currEvento?.produtos ? currEvento?.produtos.map(it => it.idProduto)[0] : defaultValues[ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID]: currEvento?.nome || defaultValues[ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID]: currEvento?.descricao || defaultValues[ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID]: currEvento?.urlTransmissao || defaultValues[ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID]: momentDataInicio ? momentDataInicio.format() : defaultValues[ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID]: momentDataInicio ? momentDataInicio.format('HH:mm') : defaultValues[ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID]: momentDataFim ? momentDataFim.format() : defaultValues[ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID],
      [ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID]: momentDataFim ? momentDataFim.format('HH:mm') : defaultValues[ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID],
    }
  }, [genericProps]);
  const { register, handleSubmit, errors, formState, control, setValue, getValues, trigger, watch } = useForm({
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: initialValues,
    criteriaMode: 'all',
  });
  const [salvando, setSalvando] = useState<boolean>(false);
  const reduxDispatch = useDispatch();
  const { tipoProduto } = watch();

  const toClose = useCallback((): void => {
    if (genericProps && genericProps.key) {
      reduxDispatch(modalsActionsClose(genericProps.key));
    }
  }, [reduxDispatch, genericProps]);

  const cancel = () => {
    toClose();
  };

  const fieldsDisabled = useCallback((): boolean => {
    return salvando;
  }, [salvando]);

  const mountData = (data:string, hora:string) => localeDateToISO(data) + 'T' + hora + ':00';

  const onSubmit = handleSubmit((fields: any) => {
    if (formState.isValid) {
      setSalvando(true);
      const values: CadastroAssembleia = {
        dataEvento:mountData(moment(fields[ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID]).format('DD/MM/YYYY'), fields[ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID]),
        descricao: fields[ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID],
        idTipoAssembleia: fields[ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID],
        idsProdutos: fields[ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID],
        titulo: fields[ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID],
        urlTransmissao: fields[ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID],
        dataFimEvento:mountData(moment(fields[ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID]).format('DD/MM/YYYY'), fields[ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID]),
      };
      if (genericProps?.assembleia) {
        reduxDispatch(
          assembleiasAssessorEditar(
            genericProps.assembleia.id,
            values,
            () => {
              setSalvando(false);
              getToastSuccess(t('AssembleiaEditModal.saveSuccessMessage'));
              toClose();
            },
            error => {
              setSalvando(false);
              AxiosErrorHandler(error);
            }
          )
        )
      } else {
        reduxDispatch(
          assembleiasAssessorCadastrar(
            values,
            (data: RetornoCadastroAssembleia) => {
              setSalvando(false);
              getToastSuccess(t('AssembleiaEditModal.registerSuccessMessage'));
              history.push(AssembleiaAssessorRoutePath.split(':id').join(data.id.toString()));
              toClose();
            },
            error => {
              setSalvando(false);
              AxiosErrorHandler(error);
            }
          )
        )
      }
    }
  });

  const getError = useCallback(
    (fieldId: string): string | null => {
      return (formState.isSubmitted || formState.touched[fieldId]) && errors[fieldId] ? errors[fieldId].message : null;
    },
    [errors, formState]
  );

  useEffect(() => {
    reduxDispatch(assembleiasAssessorLoadTipos(undefined, AxiosErrorHandler));
    reduxDispatch(assembleiasAssessorLoadTiposProduto(undefined, AxiosErrorHandler));
  }, [reduxDispatch]);

  useEffect(() => {
    if (typeof tipoProduto === 'number') {
      reduxDispatch(assembleiasAssessorLoadProdutosPorTipo(tipoProduto, undefined, AxiosErrorHandler));
    }
  }, [tipoProduto, reduxDispatch]);

  const getProdutosPorTipo = useCallback((): AssembleiaProduto[] => {
    const tipo = getValues(ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID);
    if (typeof tipo === 'number') {
      const tiposPorProduto = produtosPorTipo.filter(
        (assembleiaProdutosPorTipo: AssembleiaProdutosPorTipo): boolean => {
          return assembleiaProdutosPorTipo.idTipoProduto === tipo ? true : false;
        }
      );
      if (tiposPorProduto.length > 0) {
        return tiposPorProduto[0].produtos;
      }
    }
    return [];
  }, [produtosPorTipo, getValues]);

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
      <GenericModal
        title={t(`AssembleiaEditModal.${genericProps?.assembleia ? 'edit' : 'register'}Title`)}
        content={
          <form onSubmit={onSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name={ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID}
                  control={control}
                  rules={{
                    required: t(
                      `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID}.errors.required`
                    ) as string,
                  }}
                  as={
                    <TextField
                      label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID}.label`)}
                      className={classes.selectRoot}
                      helperText={getError(ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID)}
                      error={getError(ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID) ? true : false}
                      disabled={!!genericProps?.assembleia || fieldsDisabled() || loadingTipos || tipos.length === 0}
                      SelectProps={{
                        defaultValue: '',
                        onFocus: () => trigger(ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID),
                        IconComponent: () =>
                          loadingTipos ? (
                            <SyncIcon className="animation-rotate" />
                          ) : (
                            <InvertedChevronIcon className={classes.dropdownIcon} />
                          ),
                      }}

                      select
                    >
                      {tipos.length > 0 ? tipos.map((tipo: AssembleiaTipo, key: number) => {
                        return (
                          <MenuItem value={tipo.id} key={key}>
                            {tipo.titulo}
                          </MenuItem>
                        );
                      }) : (
                        <MenuItem value={initialValues[ASSEMBLEIA_EDIT_MODAL_TIPO_FIELD_ID]}></MenuItem>
                      )}
                    </TextField>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name={ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID}
                  control={control}
                  rules={{
                    required: t(
                      `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID}.errors.required`
                    ) as string,
                  }}
                  as={
                    <TextField
                      label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID}.label`)}
                      className={classes.selectRoot}
                      helperText={getError(ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID)}
                      error={getError(ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID) ? true : false}
                      disabled={!!genericProps?.assembleia || fieldsDisabled() || loadingTiposProduto || tipos.length === 0}
                      SelectProps={{
                        defaultValue: '',
                        onFocus: () => trigger(ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID),
                        IconComponent: () =>
                          loadingTiposProduto ? (
                            <SyncIcon className="animation-rotate" />
                          ) : (
                            <InvertedChevronIcon className={classes.dropdownIcon} />
                          ),
                      }}
                      select
                    >
                      {tiposProduto.length > 0 ? tiposProduto.map((tipo: AssembleiaProdutoTipo, key: number) => {
                        return (
                          <MenuItem value={tipo.id} key={key}>
                            {tipo.titulo}
                          </MenuItem>
                        );
                      }): (
                        <MenuItem value={initialValues[ASSEMBLEIA_EDIT_MODAL_TIPO_PRODUTO_FIELD_ID]}></MenuItem>
                      )}
                    </TextField>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name={ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID}
                  control={control}
                  rules={{
                    validate: {
                      required: (value: string[]): boolean | string => {
                        return value.length === 0
                          ? (t(
                              `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID}.errors.required`
                            ) as string)
                          : true;
                      },
                    },
                  }}
                  as={
                    <TextField
                      label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID}.label`)}
                      className={classes.selectRoot}
                      helperText={getError(ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID)}
                      error={getError(ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID) ? true : false}
                      disabled={!!genericProps?.assembleia || fieldsDisabled() || loadingProdutosPorTipo || produtosPorTipo.length === 0}
                      SelectProps={{
                        onFocus: () => trigger(ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID),
                        IconComponent: () =>
                          loadingProdutosPorTipo ? (
                            <SyncIcon className="animation-rotate" />
                          ) : (
                            <InvertedChevronIcon className={classes.dropdownIcon} />
                          ),
                      }}
                      select
                    >
                      {getProdutosPorTipo().length > 0 ? getProdutosPorTipo().map((produto: AssembleiaProduto, key: number) => {
                        return (
                          <MenuItem value={produto.idProduto} key={key} onClick={() => control.reRender()}>
                            <ListItemText primary={produto.codigo + ' - ' + produto.titulo} />
                          </MenuItem>
                        );
                      }) : (
                        <MenuItem value={initialValues[ASSEMBLEIA_EDIT_MODAL_PRODUTOS_FIELD_ID]}></MenuItem>
                      )}
                    </TextField>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID}.label`)}
                  name={ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID}
                  className={classes.selectRoot}
                  FormHelperTextProps={{
                    className: classes.nomeHelperText,
                  }}
                  inputProps={{
                    maxLength: MAX_NAME_SIZE
                  }}
                  helperText={
                    <React.Fragment>
                      <span className={classes.nomeHelperError}>{getError(ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID) || ''}</span>
                      <span className={classes.nomeHelperCounter}>
                        {(getValues(ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID) as string || '').length}/{MAX_NAME_SIZE}
                      </span>
                    </React.Fragment>
                  }
                  error={getError(ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID) ? true : false}
                  disabled={fieldsDisabled()}
                  inputRef={register({
                    required: t(
                      `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_TITULO_FIELD_ID}.errors.required`
                    ) as string,
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID}.label`)}
                  name={ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID}
                  className={classes.selectRoot}
                  FormHelperTextProps={{
                    className: classes.nomeHelperText,
                  }}
                  helperText={
                    <React.Fragment>
                      <span className={classes.nomeHelperError}>{getError(ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID) || ''}</span>
                      <span className={classes.nomeHelperCounter}>
                        {(getValues(ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID) as string || '').length}/{MAX_DESCRICAO_SIZE}
                      </span>
                    </React.Fragment>
                  }
                  inputProps={{
                    maxLength: MAX_DESCRICAO_SIZE
                  }}
                  error={getError(ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID) ? true : false}
                  disabled={fieldsDisabled()}
                  inputRef={register({
                    required: t(
                      `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_DESCRICAO_FIELD_ID}.errors.required`
                    ) as string,
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID}.label`)}
                  name={ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID}
                  className={classes.selectRoot}
                  helperText={getError(ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID)}
                  error={getError(ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID) ? true : false}
                  disabled={fieldsDisabled()}
                  inputRef={ genericProps?.assembleia ? register({
                    required: t(
                      `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID}.errors.required`
                    ) as string,
                    validate: {
                      isValid: url => {
                        return isAValidVideoUrl(url)
                          ? true
                          : (t(
                              `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID}.errors.isValid`
                            ) as string);
                      },
                    },
                  }) :
                  register({
                    validate: {
                      isValid: url => {
                        return url.length > 0 ?
                        isAValidVideoUrl(url)
                          ? true
                          : (t(
                              `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_URL_TRANSMISSAO_FIELD_ID}.errors.isValid`
                            ) as string) : true
                      },
                    },
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <input
                  name={ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID}
                  type="hidden"
                  ref={
                    register({
                      required: t(
                        `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID}.errors.required`
                      ) as string,
                      validate: {
                        isValid: value => {
                          return isAValidDate(localeDateToISO(moment(value).format('DD/MM/YYYY')))
                            ? true
                            : (t(
                                `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID}.errors.isValid`
                              ) as string);
                        },
                      },
                    })
                  }
                />
                <DatePicker
                  name={ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID}
                  label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID}.label`)}
                  value={getValues(ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID)}
                  onChange={newDate => {
                    setValue(ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID, newDate)
                  }}
                  autoOk
                  format="dd/MM/yyyy"
                  maxDate={getValues(ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID)}
                  color="primary"
                  inputProps={{
                    className: classes.dateField,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className={classes.dateFieldAdornment}>
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="inline"
                  disabled={fieldsDisabled()}
                  helperText={getError(ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID)}
                  error={getError(ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID) ? true : false}
                  disableToolbar
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  placeholder="HH:MM"
                  inputProps={{
                    maxLength: 5
                  }}
                  onChange={inputTimeMask(date => setValue(ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID, date))}
                  label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID}.label`)}
                  name={ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID}
                  className={classes.selectRoot}
                  helperText={getError(ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID)}
                  error={getError(ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID) ? true : false}
                  disabled={fieldsDisabled()}
                  inputRef={register({
                    required: t(
                      `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID}.errors.required`
                    ) as string,
                    validate: {
                      isValid: value => {
                        return isAValidTime(value)
                          ? true
                          : (t(
                              `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_HORARIO_FIELD_ID}.errors.isValid`
                            ) as string);
                      },
                    },
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <input
                  name={ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID}
                  type="hidden"
                  ref={
                    register({
                      required: t(
                        `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID}.errors.required`
                      ) as string,
                      validate: {
                        isValid: value => {
                          return isAValidDate(localeDateToISO(moment(value).format('DD/MM/YYYY')))
                            ? true
                            : (t(
                                `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID}.errors.isValid`
                              ) as string);
                        },
                      },
                    })
                  }
                />
                <DatePicker
                  name={ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID}
                  label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID}.label`)}
                  value={getValues(ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID)}
                  onChange={newDate => {
                    setValue(ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID, newDate)
                  }}
                  autoOk
                  format="dd/MM/yyyy"
                  minDate={getValues(ASSEMBLEIA_EDIT_MODAL_DATA_FIELD_ID)}
                  color="primary"
                  inputProps={{
                    className: classes.dateField,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className={classes.dateFieldAdornment}>
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="inline"
                  disabled={fieldsDisabled()}
                  helperText={getError(ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID)}
                  error={getError(ASSEMBLEIA_EDIT_MODAL_DATA_FIM_FIELD_ID) ? true : false}
                  disableToolbar
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  placeholder="HH:MM"
                  inputProps={{
                    maxLength: 5
                  }}
                  onChange={inputTimeMask(date => setValue(ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID, date))}
                  label={t(`AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID}.label`)}
                  name={ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID}
                  className={classes.selectRoot}
                  helperText={getError(ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID)}
                  error={getError(ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID) ? true : false}
                  disabled={fieldsDisabled()}
                  inputRef={register({
                    required: t(
                      `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID}.errors.required`
                    ) as string,
                    validate: {
                      isValid: value => {
                        return isAValidTime(value)
                          ? true
                          : (t(
                              `AssembleiaEditModal.fields.${ASSEMBLEIA_EDIT_MODAL_HORARIO_FIM_FIELD_ID}.errors.isValid`
                            ) as string);
                      },
                    },
                  })}
                />
              </Grid>
              
              <Grid item xs={12} sm={9} className={classes.rightActionsContent}>
                <Button variant="outlined" color="primary" onClick={cancel} disabled={salvando} startIcon={<CircleCloseIcon />}>
                  {t('AssembleiaEditModal.cancelButton')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={3} className={classes.rightActionsContent}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={salvando || !formState.isValid}
                  startIcon={<CircleCheckIcon />}
                  endIcon={salvando ? <SyncIcon className="animation-rotate" /> : null}
                >
                  {t('AssembleiaEditModal.confirmarButton')}
                </Button>
              </Grid>
            </Grid>
          </form>
        }
        genericProps={{
          maxWidth: 'sm',
          onClose: !salvando ? toClose : undefined,
        }}
      />
    </MuiPickersUtilsProvider>
  );
};
